// Export et import Excel du catalogue du matériel (03/11/2026) : une feuille par tableau de la page Catalogue, avec les
// mêmes colonnes (titres, unités, échelles c€/Wc…). Maxime peut télécharger le catalogue, le modifier dans Excel puis le
// recharger : les valeurs lues remplacent celles de la page, qu'il vérifie (résumé des changements) avant d'enregistrer.

export interface TableExcel {
  onglet: string                  // feuille : onglet de la page (« CAPEX Particulier »…)
  titre: string                   // titre du tableau dans la feuille : section de la page
  lignes: () => any[]             // lignes du catalogue (modifiées sur place à l'import)
  colonnes: any[]                 // colonnes de la page (TableCatalogue)
  fixe?: boolean                  // nombre de lignes fixe : ni ajout ni suppression à l'import
  modele?: any                    // ligne ajoutée quand le tableau en a plus
  filtre?: (l: any) => boolean    // sous-ensemble de la liste (onduleurs réseau / hybrides, câbles standard / ERP)
}

export interface CompteRendu { feuilles: number, valeurs: number, lignesAjoutees: number, lignesRetirees: number, ignorees: string[] }

type Setter = (objet: any, cle: string, valeur: any) => void

/** Titre d'une colonne dans Excel : « Agricole — Pose (j) », « Visible ». */
export function titreColonne(c: any): string {
  const titre = c.type === 'visible' ? 'Visible' : c.titre
  return (c.groupe ? c.groupe + ' — ' : '') + titre + (c.unite ? ' (' + c.unite + ')' : '')
}

function cleDe(c: any, l: any): string | undefined {
  return c.cles ? c.cles.find((k: string) => k in l) : c.cle
}

/** Valeur d'une case pour Excel : celle affichée dans la page (échelle, oui / non, libellés). */
export function versExcel(c: any, l: any): any {
  if (c.type === 'visible') return l[c.cle] === false ? 'non' : 'oui'
  if (c.type === 'case') return l[c.cle] ? 'oui' : 'non'
  if (c.type === 'caseListe') return (l[c.champ] || []).includes(c.valeur) ? 'oui' : 'non'
  if (c.type === 'multi') {
    return (l[c.cle] || []).map((v: any) => { const o = (c.options || []).find((x: any) => x.valeur === v); return o ? o.libelle : v }).join(', ')
  }
  // listes de choix : libellé affiché dans la page (Client, Raccordement…)
  if ((c.type === 'choix' || c.type === 'lecture') && c.options) {
    const o = c.options.find((x: any) => x.valeur === l[c.cle])
    return o ? o.libelle : (l[c.cle] === undefined ? null : l[c.cle])
  }
  if (c.type === 'calcule') { const v = c.valeur(l); return v == null || !isFinite(v) ? null : Math.round(v * 1e4) / 1e4 }
  if (c.derive) { const v = c.derive.get(l); return v == null ? null : v }
  const cle = cleDe(c, l)
  if (cle === undefined || !(cle in l)) return null
  const v = l[cle]
  if (v === undefined || v === null) return null
  if (c.type === 'nombre' && c.echelle) return Math.round(Number(v) * c.echelle * 1e6) / 1e6
  return v
}

/** Valeur d'une case Excel (formule : son résultat ; texte enrichi : son texte). */
function brut(v: any): any {
  if (v === undefined) return null
  if (v && typeof v === 'object') {
    if ('result' in v) return v.result
    if (Array.isArray(v.richText)) return v.richText.map((r: any) => r.text).join('')
    if ('text' in v) return v.text
  }
  return v
}

function nombre(v: any): number | null {
  if (v === null || v === '' || v === undefined) return null
  if (typeof v === 'number') return v
  const n = Number(String(v).replace(/[\s  ]/g, '').replace(',', '.'))
  return isNaN(n) ? null : n
}

/** Même valeur : vide, absent et texte vide confondus ; nombres à 1e-9 près (échelles c€/Wc). */
function egal(a: any, b: any): boolean {
  const vide = (x: any) => x === undefined || x === null || x === '' || (Array.isArray(x) && x.length === 0)
  if (vide(a) && vide(b)) return true
  if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a))
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Écrit dans la ligne la valeur lue dans Excel ; rend vrai si la valeur a changé. Colonnes calculées et en lecture seule :
 * ignorées.
 */
export function depuisExcel(c: any, l: any, v: any, set: Setter): boolean {
  v = brut(v)
  // ratio en formule dans Excel (04/11/2026) : calculé, le prix fait foi
  if (c.type === 'calcule' || c.type === 'lecture' || c.formule) return false
  const ecrire = (cle: string, x: any) => { if (egal(l[cle], x)) return false; set(l, cle, x); return true }
  if (c.type === 'visible') {
    const visible = String(v == null ? 'oui' : v).trim().toLowerCase() !== 'non'
    if (visible === (l[c.cle] !== false)) return false
    set(l, c.cle, visible)
    return true
  }
  if (c.type === 'case') {
    const coche = ['oui', 'vrai', 'x', '1', 'true'].includes(String(v == null ? '' : v).trim().toLowerCase())
    if (coche === !!l[c.cle]) return false // « non » sur un champ absent : pas de changement
    return ecrire(c.cle, coche)
  }
  if (c.type === 'caseListe') {
    const coche = ['oui', 'vrai', 'x', '1', 'true'].includes(String(v == null ? '' : v).trim().toLowerCase())
    const actuel: any[] = l[c.champ] || []
    if (coche === actuel.includes(c.valeur)) return false
    const l2 = coche ? actuel.concat([c.valeur]) : actuel.filter((x: any) => x !== c.valeur)
    const ordre: any[] = c.ordre || l2
    set(l, c.champ, ordre.filter(x => l2.includes(x)).concat(l2.filter(x => !ordre.includes(x))))
    return true
  }
  if (c.type === 'multi') {
    const libelles = String(v == null ? '' : v).split(',').map(x => x.trim().toLowerCase()).filter(x => x)
    const valeurs = (c.options || []).filter((o: any) => libelles.includes(String(o.libelle).toLowerCase()) || libelles.includes(String(o.valeur).toLowerCase()))
      .map((o: any) => o.valeur)
    return ecrire(c.cle, valeurs)
  }
  if (c.type === 'nombre') {
    const n = nombre(v)
    const x = n == null ? null : c.echelle ? n / c.echelle : n
    if (c.derive) {
      if (egal(c.derive.get(l), n)) return false
      c.derive.set(l, n, set)
      return true
    }
    const cle = cleDe(c, l)
    if (cle === undefined) return false
    return ecrire(cle, x)
  }
  if (c.type === 'choix' && c.options && v != null && v !== '') {
    const t = String(v).trim().toLowerCase()
    const o = c.options.find((x: any) => String(x.libelle).toLowerCase() === t || String(x.valeur).toLowerCase() === t)
    if (o) return ecrire(c.cle, o.valeur)
  }
  const texte = v == null || v === '' ? (c.options && c.options.some((o: any) => o.valeur === null) ? null : '') : String(v).trim()
  return ecrire(c.cle, c.type === 'fabricant' && texte ? texte.toUpperCase() : texte)
}

async function chargerExcelJS(): Promise<any> {
  // paquet du navigateur (exceljs 3), chargé à la demande
  const mod: any = await import(/* webpackChunkName: "exceljs" */ 'exceljs/dist/exceljs.min.js')
  return mod.default || mod.ExcelJS || mod
}

/** Titre d'une colonne dans la seconde ligne d'en-tête : titre et unité. */
function sousTitre(c: any): string {
  return (c.type === 'visible' ? 'Visible' : c.titre) + (c.unite ? ' (' + c.unite + ')' : '')
}

const FOND_CALCULE = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } }
const FOND_ENTETE = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEEF2F8' } }
const BORDURE = { style: 'thin', color: { argb: 'FFC9D0DB' } }
const BORDURES = { top: BORDURE, left: BORDURE, bottom: BORDURE, right: BORDURE }

// largeur de colonne au plus juste (04/11/2026) : les en-têtes passent à la ligne entre deux mots, les textes longs au-delà
// de LARGEUR_TEXTE_MAX (modèles, références), les nombres restent sur une ligne ; hauteur des lignes calculée en conséquence
const LARGEUR_MIN = 6
const LARGEUR_TEXTE_MAX = 30
const MARGE_LARGEUR = 2
const HAUTEUR_LIGNE = 15

function plusLongMot(t: any): number {
  return String(t).split(/\s+/).reduce((m, x) => Math.max(m, x.length), 0)
}

/** Largeur d'une colonne : plus long mot des en-têtes (gras), valeurs entières jusqu'à la limite, sinon leur plus long mot. */
export function largeurPour(entetes: any[], valeurs: any[]): number {
  const e = entetes.reduce((m: number, t: any) => (t === null || t === undefined ? m : Math.max(m, Math.ceil(plusLongMot(t) * 1.1))), 0)
  const v = valeurs.reduce((m: number, t: any) => {
    if (t === null || t === undefined || t === '') return m
    const n = String(t).length
    if (typeof t === 'number' || n <= LARGEUR_TEXTE_MAX) return Math.max(m, n)
    return Math.max(m, LARGEUR_TEXTE_MAX, plusLongMot(t))
  }, 0)
  return Math.max(LARGEUR_MIN, e, v) + MARGE_LARGEUR
}

/** Nombre de lignes d'un texte dans une largeur de colonne, en coupant entre les mots. */
export function lignesDans(t: any, largeur: number): number {
  if (t === null || t === undefined || t === '') return 1
  const place = Math.max(1, largeur - MARGE_LARGEUR)
  let lignes = 1, courant = 0
  String(t).split(/\s+/).forEach(mot => {
    if (courant > 0 && courant + 1 + mot.length > place) { lignes++; courant = mot.length } else courant += (courant > 0 ? 1 : 0) + mot.length
  })
  return lignes
}

/** Lettre d'une colonne Excel (1 → A, 27 → AA). */
export function lettreColonne(j: number): string {
  let l = ''
  for (let x = j; x > 0; x = Math.floor((x - 1) / 26)) l = String.fromCharCode(65 + (x - 1) % 26) + l
  return l
}

/**
 * Classeur du catalogue, au format de la page (03/11/2026) : une feuille par onglet, et dans chaque feuille un tableau
 * par section (titre, en-têtes fusionnés sur deux lignes quand les colonnes sont regroupées, une ligne par article).
 */
export async function exporterCatalogue(tables: TableExcel[], nomFichier: string): Promise<void> {
  const ExcelJS = await chargerExcelJS()
  const wb = new ExcelJS.Workbook()
  const feuilles: { [nom: string]: any } = {}
  // textes de chaque colonne, par feuille : largeurs et hauteurs ajustées à la fin (les tableaux d'une feuille partagent ses colonnes)
  const entetesCol: { [nom: string]: any[][] } = {}
  const valeursCol: { [nom: string]: any[][] } = {}
  const lignesTexte: { [nom: string]: { [r: number]: { j: number, k: number, t: any, facteur: number }[] } } = {}
  tables.forEach(t => {
    const nom = t.onglet.slice(0, 31)
    if (!feuilles[nom]) { feuilles[nom] = wb.addWorksheet(nom); entetesCol[nom] = []; valeursCol[nom] = []; lignesTexte[nom] = {} }
    const ws = feuilles[nom]
    const cols = t.colonnes
    const noterEntete = (j: number, v: any) => { (entetesCol[nom][j] = entetesCol[nom][j] || []).push(v) }
    const noterValeur = (j: number, v: any) => { (valeursCol[nom][j] = valeursCol[nom][j] || []).push(v) }
    const noterLigne = (y: number, j: number, k: number, t: any, facteur: number) => { (lignesTexte[nom][y] = lignesTexte[nom][y] || []).push({ j, k, t, facteur }) }
    cols.forEach((c, j) => noterEntete(j, sousTitre(c)))
    let r = ws.rowCount === 0 ? 1 : ws.rowCount + 2
    ws.getCell(r, 1).value = t.titre
    ws.getCell(r, 1).font = { bold: true, size: 13, color: { argb: 'FF1A1A2E' } }
    r++
    const deuxLignes = cols.some(c => c.groupe)
    if (deuxLignes) {
      let j = 0
      while (j < cols.length) {
        const c = cols[j]
        if (!c.groupe) {
          ws.getCell(r, j + 1).value = sousTitre(c)
          ws.mergeCells(r, j + 1, r + 1, j + 1)
          noterLigne(r + 1, j, j, sousTitre(c), 1.1) // cellule sur deux lignes : la seconde prend la hauteur nécessaire
          j++
          continue
        }
        let k = j
        while (k + 1 < cols.length && cols[k + 1].groupe === c.groupe) k++
        ws.getCell(r, j + 1).value = c.groupe
        if (k === j) noterEntete(j, c.groupe)
        noterLigne(r, j, k, c.groupe, 1.1)
        if (k > j) ws.mergeCells(r, j + 1, r, k + 1)
        for (let x = j; x <= k; x++) { ws.getCell(r + 1, x + 1).value = sousTitre(cols[x]); noterLigne(r + 1, x, x, sousTitre(cols[x]), 1.1) }
        j = k + 1
      }
    } else {
      cols.forEach((c, j) => { ws.getCell(r, j + 1).value = sousTitre(c); noterLigne(r, j, j, sousTitre(c), 1.1) })
    }
    const lignesEntete = deuxLignes ? 2 : 1
    for (let y = r; y < r + lignesEntete; y++) {
      cols.forEach((c, j) => {
        const cell = ws.getCell(y, j + 1)
        cell.font = { bold: true }
        cell.fill = FOND_ENTETE
        cell.border = BORDURES
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
      })
    }
    r += lignesEntete
    const lignes = t.filtre ? t.lignes().filter(t.filtre) : t.lignes()
    lignes.forEach(l => {
      const ligneExcel = r
      const ref = (cle: string) => lettreColonne(cols.findIndex(x => x.cle === cle) + 1) + ligneExcel
      cols.forEach((c, j) => {
        const v = versExcel(c, l)
        const cell = ws.getCell(r, j + 1)
        // ratios calculés dans Excel (04/11/2026), à partir des cases de la ligne
        cell.value = c.formule ? { formula: c.formule(ref, l), result: v === null || v === undefined ? '' : v } : (v === null || v === undefined ? '' : v)
        cell.border = BORDURES
        noterValeur(j, v)
        if (typeof v === 'string' && v.length > LARGEUR_TEXTE_MAX) {
          cell.alignment = { vertical: 'top', wrapText: true }
          noterLigne(r, j, j, v, 1)
        }
        if (c.type === 'calcule' || c.type === 'lecture' || c.formule) cell.font = { color: { argb: 'FF8A909C' } }
        if (c.formule || (c.inactif && c.inactif(l))) cell.fill = FOND_CALCULE
      })
      r++
    })
  })
  Object.keys(feuilles).forEach(nom => {
    const nb = Math.max(entetesCol[nom].length, valeursCol[nom].length)
    const largeurs: number[] = []
    for (let j = 0; j < nb; j++) {
      largeurs[j] = largeurPour(entetesCol[nom][j] || [], valeursCol[nom][j] || [])
      feuilles[nom].getColumn(j + 1).width = largeurs[j]
    }
    Object.keys(lignesTexte[nom]).forEach(y => {
      const n = lignesTexte[nom][Number(y)].reduce((m, x) => {
        let w = 0
        for (let i = x.j; i <= x.k; i++) w += largeurs[i] || LARGEUR_MIN
        return Math.max(m, lignesDans(x.t, w / x.facteur))
      }, 1)
      if (n > 1) feuilles[nom].getRow(Number(y)).height = n * HAUTEUR_LIGNE
    })
  })
  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = nomFichier
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(a.href), 1000)
}

function texteCase(ws: any, r: number, j: number): string {
  const v = brut(ws.getCell(r, j).value)
  return v === null || v === undefined ? '' : String(v).trim()
}

/**
 * Lit un classeur exporté (et modifié) : chaque tableau est retrouvé dans sa feuille par son titre ; ses colonnes par
 * leur titre (groupe et sous-titre) ; lignes en plus ajoutées et lignes en moins retirées, sauf pour un tableau à nombre
 * de lignes fixe.
 */
export async function importerCatalogue(fichier: ArrayBuffer, tables: TableExcel[], set: Setter): Promise<CompteRendu> {
  const ExcelJS = await chargerExcelJS()
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(fichier)
  const cr: CompteRendu = { feuilles: 0, valeurs: 0, lignesAjoutees: 0, lignesRetirees: 0, ignorees: [] }
  const ongletsConnus = new Set(tables.map(t => t.onglet.slice(0, 31)))
  wb.eachSheet((ws: any) => {
    if (!ongletsConnus.has(ws.name)) { cr.ignorees.push(ws.name); return }
    cr.feuilles++
    const duTableau = tables.filter(t => t.onglet.slice(0, 31) === ws.name)
    // lignes de titre des tableaux de la feuille
    const debuts: { r: number, t: TableExcel }[] = []
    for (let r = 1; r <= ws.rowCount; r++) {
      const titre = texteCase(ws, r, 1)
      const t = duTableau.find(x => x.titre === titre && !debuts.some(d => d.t === x))
      if (t) debuts.push({ r, t })
    }
    debuts.forEach((d, i) => {
      const t = d.t
      const fin = i + 1 < debuts.length ? debuts[i + 1].r - 1 : ws.rowCount
      const deuxLignes = t.colonnes.some(c => c.groupe)
      const h1 = d.r + 1, h2 = deuxLignes ? d.r + 2 : d.r + 1
      const largeur = Math.max(t.colonnes.length + 5, ws.columnCount || 0)
      const entetes: string[] = []
      for (let j = 1; j <= largeur; j++) {
        const a = texteCase(ws, h1, j), b = texteCase(ws, h2, j)
        entetes[j] = !deuxLignes || a === b ? b : a + ' — ' + b
      }
      const index = t.colonnes.map(c => entetes.indexOf(titreColonne(c)))
      // fichier d'un autre format (colonnes renommées) : tableau laissé tel quel, jamais vidé
      if (index.every(j => j <= 0)) { cr.ignorees.push(ws.name + ' › ' + t.titre); return }
      const donnees: any[][] = []
      for (let r = h2 + 1; r <= fin; r++) donnees.push(index.map(j => (j > 0 ? ws.getCell(r, j).value : undefined)))
      const pleine = (v: any[]) => v.some(x => brut(x) !== null && brut(x) !== '')
      // lignes vides de la fin (séparation avant le tableau suivant, article effacé dans Excel) ; un article déjà vide
      // dans la page (ex. tranche sans prix) qui reste vide dans Excel est gardé
      const toutes = t.lignes()
      const lignes = t.filtre ? toutes.filter(t.filtre) : toutes
      const articleVide = (l: any) => !l || t.colonnes.every(c => { const v = versExcel(c, l); return v === null || v === '' || v === 'oui' || v === 'non' })
      while (donnees.length && !pleine(donnees[donnees.length - 1])
        && (donnees.length > lignes.length || !articleVide(lignes[donnees.length - 1]))) donnees.pop()
      donnees.forEach((valeurs, k) => {
        let l = lignes[k]
        if (!l) {
          if (t.fixe) return
          l = JSON.parse(JSON.stringify(t.modele || {}))
          toutes.push(l)
          cr.lignesAjoutees++
        }
        t.colonnes.forEach((c, j) => { if (index[j] > 0 && depuisExcel(c, l, valeurs[j], set)) cr.valeurs++ })
      })
      if (!t.fixe && lignes.length > donnees.length) {
        cr.lignesRetirees += lignes.length - donnees.length
        lignes.slice(donnees.length).forEach((x: any) => toutes.splice(toutes.indexOf(x), 1))
      }
    })
  })
  return cr
}
